import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Trophy, Target, Users, CheckCircle2, 
  Leaf, RotateCcw, Recycle, ChevronRight, Star,
  Award, Crown, Medal, Sparkles, Check, Camera, Plus, Flame, Calendar
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import EmmeGuide, { useEmmeGuide, EmmeHelpButton } from './EmmeGuide';
import CoralReefGame from './CoralReefGame';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Lancia coriandoli
const launchConfetti = () => {
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: ['#06b6d4', '#10b981', '#f59e0b', '#8b5cf6']
  });
};

// Colori per categorie sfide
const CATEGORY_CONFIG = {
  'Riduco': { color: 'emerald', icon: Leaf, emoji: '🟢' },
  'Riuso': { color: 'blue', icon: RotateCcw, emoji: '🔵' },
  'Riciclo': { color: 'amber', icon: Recycle, emoji: '♻️' }
};

// Componente Streak
const StreakDisplay = ({ streak }) => (
  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
    streak > 0 ? 'bg-orange-500/20 border border-orange-500/50' : 'bg-slate-800'
  }`}>
    <Flame className={`w-5 h-5 ${streak > 0 ? 'text-orange-500' : 'text-slate-500'}`} />
    <span className={`font-bold ${streak > 0 ? 'text-orange-400' : 'text-slate-500'}`}>
      {streak}
    </span>
  </div>
);

// Componente Badge
const BadgeItem = ({ badge, earned = true }) => (
  <div className={`flex flex-col items-center p-2 rounded-xl transition-all ${
    earned ? 'bg-slate-800' : 'bg-slate-800/30 opacity-40'
  }`}>
    <span className="text-2xl mb-1">{badge.emoji}</span>
    <span className="text-[9px] text-slate-400 text-center leading-tight">{badge.name}</span>
  </div>
);

// Componente Calendario Attività
const ActivityCalendar = ({ weeks }) => (
  <div className="space-y-1">
    <div className="flex gap-1">
      {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((d, i) => (
        <div key={i} className="w-6 h-4 text-[8px] text-slate-500 flex items-center justify-center">{d}</div>
      ))}
    </div>
    {weeks?.map((week, wi) => (
      <div key={wi} className="flex gap-1">
        {week.map((day, di) => (
          <div
            key={di}
            className={`w-6 h-6 rounded-sm ${
              day.completed ? 'bg-emerald-500' : 'bg-slate-700'
            }`}
            title={day.date}
          />
        ))}
      </div>
    ))}
  </div>
);

// Componente Card Sfida
const ChallengeCard = ({ challenge, onComplete, loading, disabled }) => {
  const config = CATEGORY_CONFIG[challenge.category] || CATEGORY_CONFIG['Riciclo'];
  const Icon = config.icon;
  const isDisabled = challenge.completed || loading || disabled;
  
  return (
    <button
      onClick={() => !isDisabled && onComplete(challenge.id)}
      disabled={isDisabled}
      className={`w-full p-3 rounded-xl border transition-all text-left ${
        challenge.completed
          ? 'bg-emerald-500/20 border-emerald-500'
          : disabled
            ? 'bg-slate-800/30 border-slate-700/50 opacity-50'
            : 'bg-slate-800 border-slate-700 hover:border-cyan-500 active:scale-[0.98]'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          challenge.completed ? 'bg-emerald-500' : 'bg-slate-700'
        }`}>
          {challenge.completed ? (
            <CheckCircle2 className="w-6 h-6 text-white" />
          ) : (
            <Icon className="w-5 h-5 text-slate-300" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] font-medium text-slate-400">{config.emoji} {challenge.category}</span>
            <span className="text-[10px] text-cyan-400">+{challenge.points} XP</span>
          </div>
          <p className={`text-sm leading-tight ${challenge.completed ? 'text-slate-400 line-through' : 'text-white'}`}>
            {challenge.text}
          </p>
        </div>
      </div>
    </button>
  );
};

// Componente Classifica
const LeaderboardItem = ({ entry }) => (
  <div className={`flex items-center gap-2 p-2 rounded-xl ${
    entry.isCurrentUser ? 'bg-cyan-500/20 border border-cyan-500/50' : 'bg-slate-800/50'
  }`}>
    <div className={`w-6 h-6 flex items-center justify-center font-bold text-sm ${
      entry.rank === 1 ? 'text-yellow-400' : entry.rank === 2 ? 'text-slate-300' : entry.rank === 3 ? 'text-amber-600' : 'text-slate-500'
    }`}>
      {entry.rank <= 3 ? ['🥇','🥈','🥉'][entry.rank-1] : `#${entry.rank}`}
    </div>
    <div className="flex-1 min-w-0">
      <p className={`text-sm font-medium truncate ${entry.isCurrentUser ? 'text-cyan-400' : 'text-white'}`}>
        {entry.displayName}
      </p>
    </div>
    <p className="font-bold text-white">{entry.points}</p>
  </div>
);

// Componente principale
const GamificationSection = ({ onNavigateToOldHome, onStartCollection }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('sfide');
  const [showCelebration, setShowCelebration] = useState(false);
  
  // Guida Emme
  const { showGuide, closeGuide, openGuide } = useEmmeGuide('gamification');

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [dashRes, leaderRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/gamification/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/gamification/leaderboard`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setDashboard(dashRes.data);
      setLeaderboard(leaderRes.data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCompleteChallenge = async (challengeId) => {
    if (completing) return;
    setCompleting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/gamification/complete-challenge`, { challenge_id: challengeId }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Celebrazione!
      launchConfetti();
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 3000);
      
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const streak = dashboard?.streak || 0;
  const badges = dashboard?.badges || [];

  return (
    <div className="max-w-4xl mx-auto p-4 pb-24">
      {/* Guida Emme */}
      {showGuide && <EmmeGuide sectionId="gamification" onClose={closeGuide} />}
      <EmmeHelpButton onClick={openGuide} />
      
      {/* Celebrazione */}
      {showCelebration && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-emerald-500 text-white px-8 py-6 rounded-3xl shadow-2xl animate-bounce">
            <p className="text-3xl font-bold text-center">🎉 Fantastico!</p>
            <p className="text-center">+10 XP guadagnati!</p>
          </div>
        </div>
      )}

      {/* Header con Streak */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-white">Ciao Eroe! 👋</h1>
          <p className="text-cyan-400 text-sm">Proteggiamo il mare insieme</p>
        </div>
        <StreakDisplay streak={streak} />
      </div>

      {/* Barriera Corallina Interattiva */}
      <div className="mb-4">
        <CoralReefGame 
          completedChallenges={dashboard?.user?.total_challenges || 0}
          daysSinceLastChallenge={dashboard?.days_since_last || 0}
        />
      </div>

      {/* Pulsante Nuova Raccolta */}
      <button
        onClick={onStartCollection}
        className="w-full p-4 mb-4 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 rounded-2xl text-white font-bold flex items-center justify-center gap-3 shadow-lg active:scale-[0.98]"
      >
        <Camera className="w-6 h-6" />
        <span>Nuova Raccolta</span>
        <Plus className="w-5 h-5" />
      </button>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-[#1e293b] rounded-xl p-3 text-center">
          <Trophy className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-white">{dashboard?.user?.points || 0}</p>
          <p className="text-[9px] text-slate-400">XP Totali</p>
        </div>
        <div className="bg-[#1e293b] rounded-xl p-3 text-center">
          <Target className="w-5 h-5 text-purple-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-white">{dashboard?.completed_today >= 1 ? '✓' : '○'}</p>
          <p className="text-[9px] text-slate-400">Sfida Oggi</p>
        </div>
        <div className="bg-[#1e293b] rounded-xl p-3 text-center">
          <Award className="w-5 h-5 text-amber-400 mx-auto mb-1" />
          <p className="text-lg font-bold text-white">#{dashboard?.user?.rank || '-'}</p>
          <p className="text-[9px] text-slate-400">Classifica</p>
        </div>
      </div>

      {/* Badge Section */}
      {badges.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-slate-500 mb-2">🏅 I tuoi badge</p>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {badges.map(badge => (
              <BadgeItem key={badge.id} badge={badge} />
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex bg-[#1e293b] rounded-xl p-1 mb-4">
        {[
          { id: 'sfide', label: 'Sfida', icon: Target },
          { id: 'classifica', label: 'Classifica', icon: Trophy },
          { id: 'attivita', label: 'Attività', icon: Calendar }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-2 rounded-lg font-medium text-sm flex items-center justify-center gap-1 ${
              activeTab === tab.id ? 'bg-cyan-600 text-white' : 'text-slate-400'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Sfide */}
      {activeTab === 'sfide' && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500 mb-2">🎯 Scegli 1 sfida da completare oggi</p>
          {dashboard?.daily_challenges?.map(challenge => (
            <ChallengeCard
              key={challenge.id}
              challenge={challenge}
              onComplete={handleCompleteChallenge}
              loading={completing}
              disabled={!dashboard?.can_complete_today && !challenge.completed}
            />
          ))}
          {dashboard?.completed_today >= 1 && (
            <div className="bg-emerald-500/20 border border-emerald-500 rounded-xl p-4 text-center mt-4">
              <Star className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <p className="text-emerald-400 font-bold">Sfida completata! 🎉</p>
              <p className="text-slate-400 text-xs">Torna domani per una nuova sfida</p>
            </div>
          )}
        </div>
      )}

      {/* Tab: Classifica */}
      {activeTab === 'classifica' && (
        <div className="space-y-2">
          <p className="text-xs text-slate-500 mb-2">🏆 Classifica mensile</p>
          {leaderboard.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Nessun partecipante</p>
          ) : (
            leaderboard.slice(0, 10).map(entry => (
              <LeaderboardItem key={entry.rank} entry={entry} />
            ))
          )}
        </div>
      )}

      {/* Tab: Attività */}
      {activeTab === 'attivita' && (
        <div>
          <p className="text-xs text-slate-500 mb-3">📊 Ultime 4 settimane</p>
          <div className="bg-[#1e293b] rounded-xl p-4">
            <ActivityCalendar weeks={dashboard?.activity_calendar?.weeks} />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="bg-[#1e293b] rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-orange-400">{streak}</p>
              <p className="text-xs text-slate-400">🔥 Giorni consecutivi</p>
            </div>
            <div className="bg-[#1e293b] rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-emerald-400">{dashboard?.user?.challenges_completed || 0}</p>
              <p className="text-xs text-slate-400">✅ Sfide totali</p>
            </div>
          </div>
        </div>
      )}

      {/* Link Dashboard */}
      <button
        onClick={onNavigateToOldHome}
        className="w-full mt-4 py-3 text-slate-500 text-sm hover:text-slate-300"
      >
        Vai alla Dashboard Completa →
      </button>
    </div>
  );
};

export default GamificationSection;
